import { ReactElement } from "react";
import { Input } from "@chakra-ui/react";
import * as yup from "yup";
import type { ControllerProps, UseFormRegisterReturn } from "react-hook-form";
import { StringFilter } from "__generated__/gql/graphql";
import { assertIsString, getAssertedStringArray } from "utils/assertions";
import { OptionalKeys } from "utils/typescriptUtilis";
import isoDateStr from "utils/schemas/isoDateStr";
import decimalStr from "utils/schemas/decimalStr";
import BooleanRadioButtons from "components/Shared/Forms/BooleanRadioButtons";
import NumberInput from "components/Shared/Forms/NumberInput";
import PercentInput from "components/Shared/Forms/PercentInput";
import {
  renderDateFilterInput,
  renderDateTimeFilterInput,
} from "components/Shared/FilterEditor/DateFilterInput";
import {
  ControlledInputFilterDefinition,
  FilterDefinition,
  FilterVariantDefinition,
  FilterVariantDefinitionOverrides,
  getFilterVariantDefinitionsWithOverrides,
  GetResultContext,
  GetResultFunction,
  RegisterInputFilterDefinition,
} from "./utils";

export const equalityFilterVariants: FilterVariantDefinition[] = [
  { id: "equalTo", label: "Is" },
  { id: "notEqualTo", label: "Is not" },
];

export const defaultStringFilterVariants: FilterVariantDefinition[] = [
  { id: "includesInsensitive", label: "Contains" },
  { id: "notIncludesInsensitive", label: "Does not contain" },
  { id: "equalToInsensitive", label: "Is" },
  { id: "notEqualToInsensitive", label: "Is not" },
  { id: "startsWithInsensitive", label: "Starts with" },
  { id: "notStartsWithInsensitive", label: "Does not start with" },
  { id: "endsWithInsensitive", label: "Ends with" },
  { id: "notEndsWithInsensitive", label: "Does not end with" },
];

export const notStringFilterVariants: {
  [variantId: string]: string | undefined;
} = {
  notIncludesInsensitive: "includesInsensitive",
  notEqualToInsensitive: "equalToInsensitive",
  notStartsWithInsensitive: "startsWithInsensitive",
  notEndsWithInsensitive: "endsWithInsensitive",
  notIn: "in",
};

export function getDefaultStringFilterVariants(
  overrides?: FilterVariantDefinitionOverrides,
) {
  return getFilterVariantDefinitionsWithOverrides(
    defaultStringFilterVariants,
    overrides,
  );
}

type GetFilterDefinitionOptions = (
  | OptionalKeys<RegisterInputFilterDefinition, "variants">
  | OptionalKeys<ControlledInputFilterDefinition, "variants">
) & {
  variantOverrides?: FilterVariantDefinitionOverrides;
};

export function getStringFilterDefinition(
  options: GetFilterDefinitionOptions,
): FilterDefinition {
  const { variantOverrides, ...definition } = options;
  return {
    defaultVariant: "includesInsensitive",
    equalityVariant: "equalToInsensitive",
    schema: yup.string().ensure().trim(),
    variants: getFilterVariantDefinitionsWithOverrides(
      defaultStringFilterVariants,
      variantOverrides,
    ),
    ...definition,
  };
}

export type SelectFilterDefinitionOptions = GetFilterDefinitionOptions & {
  getSelectResult: GetResultFunction;
  renderSelectInput: (
    formProps: Parameters<ControllerProps["render"]>[0],
    variantProps: { isMulti: boolean; variant: string },
  ) => ReactElement;
  selectSchema?: yup.AnySchema;
};

const baseSelectSchema = yup.array().ensure().min(1);

export function getSelectFilterDefinition(
  options: SelectFilterDefinitionOptions,
): FilterDefinition {
  const {
    getSelectResult,
    renderSelectInput,
    selectSchema,
    variantOverrides,
    ...definition
  } = options;

  const getSelectVariant = (
    baseDef: FilterVariantDefinition,
  ): FilterVariantDefinition => ({
    getResult: getSelectResult,
    renderControlledInput: (formProps) =>
      renderSelectInput(formProps, { isMulti: true, variant: "in" }),
    schema: selectSchema
      ? baseSelectSchema.of(selectSchema.label("Value"))
      : baseSelectSchema,
    ...baseDef,
  });

  return {
    defaultVariant: "in",
    equalityVariant: "in",
    variants: [
      getSelectVariant({ id: "in", label: "Is one of" }),
      getSelectVariant({ id: "notIn", label: "Is not one of" }),
    ],
    ...definition,
  };
}

export function getStringSelectFilterDefinition(
  options: SelectFilterDefinitionOptions,
): FilterDefinition {
  const { variantOverrides, ...definition } = options;
  const stringFilter = getStringFilterDefinition(definition);
  const selectFilter = getSelectFilterDefinition(definition);
  return {
    ...stringFilter,
    ...selectFilter,
    variants: getFilterVariantDefinitionsWithOverrides(
      [
        ...selectFilter.variants,
        ...stringFilter.variants.filter(
          (v) =>
            ![
              "equalTo",
              "notEqualTo",
              "equalToInsensitive",
              "notEqualToInsensitive",
              "in",
              "notIn",
            ].includes(v.id),
        ),
      ],
      variantOverrides,
    ),
  };
}

export const defaultNumericFilterVariants: FilterVariantDefinition[] = [
  { id: "equalTo", label: "Is" },
  { id: "notEqualTo", label: "Is not" },
  { id: "lessThan", label: "Is less than" },
  { id: "lessThanOrEqualTo", label: "Is less than or equal to" },
  { id: "greaterThan", label: "Is greater than" },
  { id: "greaterThanOrEqualTo", label: "Is greater than or equal to" },
];

type GetNumericFilterDefinitionOptions = Omit<
  GetFilterDefinitionOptions,
  "renderInput" | "renderControlledInput"
> & {
  renderInput?: ControllerProps["render"];
};

const defaultRenderNumberInput: ControllerProps["render"] = ({ field }) => (
  <NumberInput {...field} thousandSeparator />
);

export function getNumberFilterDefinition(
  options: GetNumericFilterDefinitionOptions,
): FilterDefinition {
  const {
    renderInput = defaultRenderNumberInput,
    variantOverrides,
    ...definition
  } = options;
  return {
    defaultVariant: "equalTo",
    equalityVariant: "equalTo",
    renderControlledInput: renderInput,
    schema: decimalStr(),
    variants: getFilterVariantDefinitionsWithOverrides(
      defaultNumericFilterVariants,
      variantOverrides,
    ),
    ...definition,
  };
}

const defaultRenderIntegerInput: ControllerProps["render"] = ({ field }) => (
  <NumberInput {...field} thousandSeparator decimalScale={0} />
);

export function getIntegerFilterDefinition(
  options: GetNumericFilterDefinitionOptions,
): FilterDefinition {
  const { renderInput = defaultRenderIntegerInput, ...definition } = options;
  return getNumberFilterDefinition({
    renderInput,
    schema: yup.number().integer(),
    ...definition,
  });
}

const defaultRenderCurrencyInput: ControllerProps["render"] = ({ field }) => (
  <NumberInput
    {...field}
    thousandSeparator
    decimalScale={2}
    fixedDecimalScale
    prefix="$"
  />
);

export function getCurrencyFilterDefinition(
  options: GetNumericFilterDefinitionOptions,
): FilterDefinition {
  const { renderInput = defaultRenderCurrencyInput, ...definition } = options;
  return getNumberFilterDefinition({ renderInput, ...definition });
}

const defaultRenderPercentInput: ControllerProps["render"] = ({ field }) => (
  <PercentInput {...field} />
);

export function getPercentFilterDefinition(
  options: GetNumericFilterDefinitionOptions,
): FilterDefinition {
  const { renderInput = defaultRenderPercentInput, ...definition } = options;
  return getNumberFilterDefinition({ renderInput, ...definition });
}

export const defaultDateFilterVariants: FilterVariantDefinition[] = [
  ...getFilterVariantDefinitionsWithOverrides(defaultNumericFilterVariants, {
    lessThan: { label: "Is before" },
    greaterThan: { label: "Is after" },
    lessThanOrEqualTo: { label: "Is on or before" },
    greaterThanOrEqualTo: { label: "Is on or after" },
  }),
];

export function getDateFilterDefinition(
  options: GetFilterDefinitionOptions,
): FilterDefinition {
  const { variantOverrides, ...definition } = options;
  return {
    defaultVariant: "equalTo",
    equalityVariant: "equalTo",
    renderControlledInput: renderDateFilterInput,
    schema: isoDateStr(),
    variants: getFilterVariantDefinitionsWithOverrides(
      defaultDateFilterVariants,
      variantOverrides,
    ),
    ...definition,
  };
}

export function getDateTimeFilterDefinition(
  options: GetFilterDefinitionOptions,
): FilterDefinition {
  const { variantOverrides, ...definition } = options;
  return {
    defaultVariant: "equalTo",
    equalityVariant: "equalTo",
    renderControlledInput: renderDateTimeFilterInput,
    schema: isoDateStr({ isDateTime: true }),
    variants: getFilterVariantDefinitionsWithOverrides(
      defaultDateFilterVariants,
      variantOverrides,
    ),
    ...definition,
  };
}

export const defaultBooleanFilterVariants: FilterVariantDefinition[] = [
  { id: "equalTo", label: "Is" },
  { id: "notEqualTo", label: "Is not" },
];

const renderBooleanInput: ControllerProps["render"] = ({ field }) => (
  <BooleanRadioButtons
    {...field}
    variant="outline"
    colorScheme="softBlueButton"
    isAttached
    width="100%"
    buttonProps={{
      height: "auto",
      py: ".83rem",
      px: 6,
      flex: "1 0 auto",
      ml: "-1px",
      borderRadius: "md",
      _first: { ml: 0 },
      _hover: { bg: "softBlueButton.100" },
      _checked: {
        bg: "softBlue",
        borderColor: "softBlue",
        color: "white",
      },
    }}
  />
);

export function getBooleanFilterDefinition(
  options: GetFilterDefinitionOptions,
) {
  const { variantOverrides, ...definition } = options;
  return {
    defaultVariant: "equalTo",
    equalityVariant: "equalTo",
    renderControlledInput: renderBooleanInput,
    schema: yup.bool().nullable(),
    variants: getFilterVariantDefinitionsWithOverrides(
      defaultBooleanFilterVariants,
      variantOverrides,
    ),
    ...definition,
  };
}

export const renderMonoInput = (props: UseFormRegisterReturn) => (
  <Input
    {...props}
    fontFamily="mono"
    fontSize=".9em"
    fontWeight={500}
    color="gray.600"
    autoComplete="off"
    autoCorrect="off"
    autoCapitalize="off"
    spellCheck="false"
  />
);

export const defaultUuidFilterVariants: FilterVariantDefinition[] = [
  { id: "in", label: "Is" },
  { id: "notIn", label: "Is not" },
];

export function getUuidFilterDefinition(
  options: GetFilterDefinitionOptions,
): FilterDefinition {
  const { variantOverrides, ...definition } = options;
  return {
    defaultVariant: "in",
    equalityVariant: "in",
    renderInput: renderMonoInput,
    schema: yup
      .array()
      .of(yup.string().uuid("Each value must be a valid UUID"))
      .transform((value, originalValue) => {
        if (typeof originalValue === "string") {
          return originalValue
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
        }

        if (Array.isArray(originalValue)) {
          return getAssertedStringArray(originalValue);
        }
      })
      .ensure(),
    variants: getFilterVariantDefinitionsWithOverrides(
      defaultUuidFilterVariants,
      variantOverrides,
    ),
    ...definition,
  };
}

type ManyFilterKeys<TFilter> = {
  [K in keyof TFilter as keyof NonNullable<TFilter[K]> extends
    | "some"
    | "none"
    | "every"
    | "aggregates"
    ? K
    : never]: TFilter[K];
};

type ManyFilterBaseType<
  TFilter,
  K extends keyof ManyFilterKeys<TFilter>,
  K2 extends keyof NonNullable<TFilter[K]> = keyof NonNullable<TFilter[K]>,
> = NonNullable<NonNullable<TFilter[K]>[K2]>;

export function createMakeGetManyFilterResult<TFilter>() {
  return function makeGetManyFilterResult<
    K extends keyof ManyFilterKeys<TFilter>,
  >(
    manyFieldKey: K,
    getInnerFilter: (
      innerFilter: StringFilter,
    ) => ManyFilterBaseType<TFilter, K>,
  ) {
    const getManyFilterResult: GetResultFunction<unknown, TFilter> = (
      value,
      { filterVariantDefinition: { id: variantId } },
    ): TFilter => {
      const inverseVariant = notStringFilterVariants[variantId];
      const manyFieldFilterKey = inverseVariant ? "none" : "some";
      return {
        [manyFieldKey]: {
          [manyFieldFilterKey]: getInnerFilter({
            [inverseVariant || variantId]: value,
          }),
        },
      } as TFilter;
    };
    return getManyFilterResult;
  };
}

export const SEARCH_FILTER_ID = "search";

export const SEARCH_FILTER_VARIANT = "includesInsensitive";

export function getSearchFilterDefinition(
  options: OptionalKeys<GetFilterDefinitionOptions, "id" | "label">,
): FilterDefinition {
  const { variantOverrides, ...definition } = options;
  return {
    id: SEARCH_FILTER_ID,
    label: "Search",
    isHidden: true,
    defaultVariant: SEARCH_FILTER_VARIANT,
    equalityVariant: SEARCH_FILTER_VARIANT,
    schema: yup.string().ensure().trim(),
    variants: getFilterVariantDefinitionsWithOverrides(
      [{ id: "includesInsensitive", label: "Contains" }],
      variantOverrides,
    ),
    ...definition,
  };
}

export function makeCombinedSearchFilter<FilterInput>(
  nestedFilterDefinitions: (
    | (FilterDefinition | undefined)
    | FilterDefinition[]
  )[],
  getBaseResult?: (search: string, context: GetResultContext) => FilterInput,
) {
  const searchFilters = nestedFilterDefinitions.reduce<FilterDefinition[]>(
    (acc, defOrDefs) => {
      const fd = Array.isArray(defOrDefs)
        ? defOrDefs.find((f) => f.id.endsWith(SEARCH_FILTER_ID))
        : defOrDefs;

      if (fd) acc.push(fd);

      return acc;
    },
    [],
  );

  return function getResult(search: unknown, context: GetResultContext) {
    assertIsString(search);

    const ors: FilterInput[] = getBaseResult
      ? [getBaseResult(search, context)]
      : [];

    searchFilters.forEach((fd) => {
      const result = fd.getResult?.(search, context);
      if (result) ors.push(result as FilterInput);
    });

    if (!ors.length) return;

    if (ors.length === 1) return ors[0];

    return { or: ors };
  };
}
